/**
 * Small, dependency-free typo-tolerant matcher for short reference lists
 * (manufacturer names and the like) -- used by Combobox to resolve what
 * someone typed to an existing option even with a minor typo, while still
 * leaving a genuinely new/unrecognised name alone so "+ Add ... as new"
 * stays the way to create one. Never fires on its own -- only called at
 * the point a caller decides to commit a typed value.
 */

/** Damerau-Levenshtein (optimal string alignment): like plain edit
 * distance, but counts an adjacent-character transposition ("extorn" ->
 * "extron") as a single edit rather than two -- the most common real
 * typo shape, and worth the small extra check over plain Levenshtein. */
function editDistance(a: string, b: string): number {
  const m = a.length;
  const n = b.length;
  if (m === 0) return n;
  if (n === 0) return m;
  const d: number[][] = Array.from({ length: m + 1 }, () => new Array(n + 1).fill(0));
  for (let i = 0; i <= m; i++) d[i]![0] = i;
  for (let j = 0; j <= n; j++) d[0]![j] = j;
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      d[i]![j] = Math.min(d[i - 1]![j]! + 1, d[i]![j - 1]! + 1, d[i - 1]![j - 1]! + cost);
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        d[i]![j] = Math.min(d[i]![j]!, d[i - 2]![j - 2]! + cost);
      }
    }
  }
  return d[m]![n]!;
}

/** How many edits a name of this length can absorb before a "fuzzy match"
 * risks being a different, shorter name instead of a typo of this one --
 * short names (AMX, LG, JBL) get zero tolerance, since a single edit
 * there usually lands on a real, different, equally-short name. */
function typoTolerance(labelLength: number): number {
  if (labelLength <= 3) return 0;
  if (labelLength <= 6) return 1;
  return 2;
}

/**
 * Returns the single item whose label is a confident typo-distance match
 * for `query`, or null if nothing is close enough or more than one item
 * ties for closest (ambiguous -- never guess between two candidates).
 */
export function closestFuzzyMatch<T>(query: string, items: T[], getLabel: (item: T) => string): T | null {
  const needle = query.trim().toLowerCase();
  if (!needle) return null;

  let best: { item: T; dist: number } | null = null;
  let ambiguous = false;
  for (const item of items) {
    const label = getLabel(item).trim().toLowerCase();
    if (!label) continue;
    const tolerance = typoTolerance(label.length);
    if (tolerance === 0) continue; // exact match only -- handled separately by the caller
    const dist = editDistance(needle, label);
    if (dist > tolerance) continue;
    if (!best || dist < best.dist) {
      best = { item, dist };
      ambiguous = false;
    } else if (dist === best.dist) {
      ambiguous = true;
    }
  }
  return best && !ambiguous ? best.item : null;
}

/**
 * Returns up to `limit` items whose label is reasonably close to `query`,
 * ranked closest first -- for presenting a "did you mean" picker when
 * nothing matched confidently enough to auto-resolve (unlike
 * closestFuzzyMatch, this never needs a single unique winner: showing a
 * few candidates for a human to pick from is fine even when more than one
 * is close). A candidate must differ by no more than half its own length
 * in edits, or it isn't a plausible typo of the query at all.
 */
export function topFuzzyMatches<T>(query: string, items: T[], getLabel: (item: T) => string, limit = 4): T[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return [];

  return items
    .map((item) => {
      const label = getLabel(item).trim().toLowerCase();
      if (!label) return null;
      const dist = editDistance(needle, label);
      const maxLen = Math.max(needle.length, label.length);
      return dist / maxLen <= 0.5 ? { item, dist } : null;
    })
    .filter((s): s is { item: T; dist: number } => s !== null)
    .sort((a, b) => a.dist - b.dist)
    .slice(0, limit)
    .map((s) => s.item);
}
